import type { GalleryProps } from "./Gallery"
import InfoSection from "./InfoSection"
import { InfoSectionStickyFooter } from "./StickyFooter"
import { useGetContextProduct } from "../../../../cart/hooks"

import AddToCartButton from "@components/elements/V2/Buttons/AddToCartButton"
import { NumberDisplayValueProps } from "@components/elements/V2/NumberDisplay"
import { hasDiscountedPrice } from "@utils/shop"
import React, { useEffect, useState, useRef } from "react"

type Props = {
  consolidatedProduct: YocoCom.ConsolidatedPurchasableItem
  productName: string
  productDescription?: string
  descriptionHeader?: string
  price: NumberDisplayValueProps
  waitlistLink?: string | YocoCom.YocoLink
  productUrl: string | YocoCom.YocoLink | undefined
  contentSections: Storyblok.InfoSection[]
  recommendedProducts?: (Storyblok.Product | Storyblok.Bundle)[]
  showRecommendedProducts: boolean
  tags?: Storyblok.ProductTag[]
  selectedVariantOption?: number
  onVariantClick?: (index: number) => void
  mobileGridOrder?: number
  badge?: YocoCom.Badge
  paymentStrip?: string
  showRatings: boolean
} & GalleryProps

const ProductInfoSection = ({
  consolidatedProduct,
  productName,
  productDescription,
  descriptionHeader,
  price,
  waitlistLink,
  productUrl,
  contentSections,
  galleryItems,
  tags,
  mobileGridOrder,
  badge,
  paymentStrip,
  showRatings = true,
}: Props) => {
  const contextProduct = useGetContextProduct(consolidatedProduct)
  const initialQuantity = consolidatedProduct?.quantity
  const [quantity, setQuantity] = useState(initialQuantity ?? 1)

  useEffect(() => {
    setQuantity(contextProduct?.quantity ?? 1)
  }, [contextProduct?.quantity])

  const scrollRef = useRef<HTMLDivElement>(null)

  const handleClick = () => {
    setTimeout(() => {
      scrollRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      })
    }, 100)
  }

  const getPriceValue = () => {
    if (typeof price?.value === "string") {
      return Number.parseFloat(price.value)
    }
    if (typeof price?.value === "number") {
      return price.value
    }
    return undefined
  }

  const footerContent = (
    <InfoSectionStickyFooter>
      <div className="flex flex-col gap-12 lg:flex-row">
        <AddToCartButton
          style="solid"
          purchasableItem={consolidatedProduct}
          quantity={quantity}
          changeCartQuantity={quantity !== contextProduct?.quantity}
          reasonNotAvailable={consolidatedProduct.reasonNotAvailable}
          className="!w-full"
          price={getPriceValue()}
          waitlistLink={waitlistLink}
          onClick={handleClick}
        />
      </div>
    </InfoSectionStickyFooter>
  )

  return (
    <InfoSection
      mobileGridOrder={mobileGridOrder}
      galleryItems={galleryItems}
      name={productName}
      description={productDescription}
      productUrl={productUrl}
      contentSections={contentSections}
      price={consolidatedProduct.price}
      originalPrice={consolidatedProduct.originalPrice}
      priceSubscriptText={consolidatedProduct.priceSubScript}
      onPromotion={hasDiscountedPrice(
        consolidatedProduct.price,
        consolidatedProduct.originalPrice
      )}
      footerContent={footerContent}
      overrideShowDiscount={false}
      badge={badge}
      tags={tags}
      skus={consolidatedProduct.reviewSkus}
      recommendedProducts={consolidatedProduct.recommendedProducts}
      paymentStrip={paymentStrip}
      descriptionHeader={descriptionHeader}
      showRatings={showRatings}
      boxContent={consolidatedProduct.boxContent}
      disclaimer={consolidatedProduct.disclaimer}
      suitedFor={consolidatedProduct.suitedFor}
      industries={consolidatedProduct.industries}
    />
  )
}

export default ProductInfoSection
